import { api } from "@/lib/api-client"

// =============================================================================
// Usage analytics (Fas 6)
// =============================================================================

/** Token/SEK bucket for one period (day / ISO week / month). */
export type HostedAgentUsageBucket = {
  period: string
  runs: number
  promptTokens: number
  completionTokens: number
  totalTokens: number
  costSek: number
}

/** Per-agent aggregate from the durable run-posts (selftests excluded). */
export type HostedAgentUsageAgent = {
  agentId: string
  name: string | null
  slug: string | null
  runs: number
  promptTokens: number
  completionTokens: number
  totalTokens: number
  costSek: number
  avgLatencyMs: number | null
  statusCounts: Record<string, number>
  selftestRuns: number
  daily: HostedAgentUsageBucket[]
  weekly: HostedAgentUsageBucket[]
  monthly: HostedAgentUsageBucket[]
}

export type HostedAgentUsageResponse = {
  currency: "SEK"
  startDate: string
  endDate: string
  summary: {
    runs: number
    promptTokens: number
    completionTokens: number
    totalTokens: number
    costSek: number
    avgLatencyMs: number | null
    selftestRuns: number
  }
  agents: HostedAgentUsageAgent[]
}

/**
 * Per-agent hosted-agent usage (GET /api/admin/hosted-agents/usage):
 * tokens and SEK per day/week/month plus run counts and average latency,
 * aggregated from the durable run-posts. SEK-native; selftest runs are
 * excluded from every aggregate and reported separately.
 */
export async function getHostedAgentUsage(
  rangeDays: 7 | 30 = 30
): Promise<HostedAgentUsageResponse> {
  const response = await api.get(`/admin/hosted-agents/usage?range=${rangeDays}`)
  return response.data || response
}

// ---------------------------------------------------------------------------
// Invoice basis (#3039): GET /api/admin/hosted-agents/usage/invoice?month=YYYY-MM
// ---------------------------------------------------------------------------

/** One invoice line: an agent's billable month, straight from the run posts. */
export type HostedAgentInvoiceLine = {
  agentId: string
  name: string | null
  slug: string | null
  runs: number
  userRuns: number
  routineRuns: number
  completedRuns: number
  failedRuns: number
  /** Selftest + eval runs: platform measurements, never billed. */
  excludedRuns: number
  heavyRuns: number
  lightRuns: number
  promptTokens: number
  completionTokens: number
  totalTokens: number
  costSek: number
  costPerRunP50Sek: number
  costPerRunMaxSek: number
}

export type HostedAgentInvoice = {
  currency: "SEK"
  month: string
  startDate: string
  endDate: string
  heavyRunSek: number
  lightRunSek: number
  lines: HostedAgentInvoiceLine[]
  totals: Omit<
    HostedAgentInvoiceLine,
    "agentId" | "name" | "slug" | "costPerRunP50Sek" | "costPerRunMaxSek"
  >
}

export async function getHostedAgentInvoice(month: string): Promise<HostedAgentInvoice> {
  const response = await api.get(
    `/admin/hosted-agents/usage/invoice?month=${encodeURIComponent(month)}`
  )
  return (response.data || response) as HostedAgentInvoice
}

/** The same month as CSV (semicolon, decimal comma, BOM — Swedish Excel opens it as-is). */
export async function downloadHostedAgentInvoiceCsv(month: string): Promise<Blob> {
  const response = await api.get(
    `/admin/hosted-agents/usage/invoice?month=${encodeURIComponent(month)}&format=csv`,
    { responseType: "blob" }
  )
  return (response.data || response) as Blob
}
